<template>
  <div class="food-tinder-container">
    <!-- Start -->
    <div v-if="phase === 'idle'" class="start-screen">
      <div class="text-5xl mb-3">😋</div>
      <h2 class="text-xl font-bold text-white mb-2">Food Tinder</h2>
      <p class="text-white/60 text-sm mb-5">Beantworte ein paar Fragen und wir finden dein perfektes Gericht!</p>
      <button @click="start" :disabled="loading" class="start-btn">
        <span v-if="loading" class="animate-spin mr-2">⏳</span>
        Los geht's! 🔥
      </button>
    </div>

    <!-- Questions Phase -->
    <div v-if="phase === 'swiping'" class="swipe-phase">
      <div class="progress-dots mb-6">
        <span
          v-for="(q, i) in questions"
          :key="i"
          class="dot"
          :class="{
            'dot-done': i < currentIndex,
            'dot-active': i === currentIndex,
            'dot-upcoming': i > currentIndex,
          }"
        ></span>
      </div>

      <!-- Question Card -->
      <div class="card-stack">
        <div
          v-if="currentQuestion"
          class="question-card"
          :key="currentIndex"
          :style="cardStyle"
          @touchstart="onTouchStart"
          @touchmove="onTouchMove"
          @touchend="onTouchEnd"
          @mousedown="onMouseDown"
        >
          <!-- Swipe indicators -->
          <div
            v-if="swipeDir === 'right'"
            class="swipe-label yes-label"
          >JA! 👍</div>
          <div
            v-if="swipeDir === 'left'"
            class="swipe-label no-label"
          >NEIN 👎</div>

          <div class="text-6xl mb-4">{{ currentQuestion.emoji }}</div>
          <h3 class="text-xl font-bold text-white leading-relaxed text-center px-4">
            {{ currentQuestion.question }}
          </h3>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons mt-8">
        <button @click="answerNo" class="answer-btn no-btn">
          <span class="text-2xl">👎</span>
          <span class="text-sm">Nein</span>
        </button>
        <button @click="answerYes" class="answer-btn yes-btn">
          <span class="text-2xl">👍</span>
          <span class="text-sm">Ja!</span>
        </button>
      </div>
    </div>

    <!-- Loading Results -->
    <div v-if="phase === 'analyzing'" class="analyzing-screen">
      <div class="analyzing-animation">
        <div class="analyze-ring"></div>
        <span class="text-3xl absolute">🔍</span>
      </div>
      <p class="text-white font-medium mt-4">Analysiere deinen Geschmack...</p>
      <p class="text-white/50 text-sm mt-1">Die KI erstellt dein perfektes Menü</p>
    </div>

    <!-- Results -->
    <div v-if="phase === 'results'" class="results-screen">
      <div class="text-center mb-5">
        <div class="text-4xl mb-2">🎯</div>
        <h2 class="text-xl font-bold text-white">Dein perfektes Menü!</h2>
        <p class="text-white/60 text-sm mt-1">{{ recommendation?.tagline }}</p>
      </div>

      <div class="space-y-3 mb-6">
        <div
          v-for="item in recommendation?.items"
          :key="item.slot"
          class="result-item"
        >
          <div class="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 bg-white/10">
            <img
              v-if="getProductImage(item.product)"
              :src="getProductImage(item.product)"
              :alt="item.product.name"
              class="w-full h-full object-cover"
              @error="$event.target.style.display='none'"
            />
            <div v-else class="w-full h-full flex items-center justify-center text-2xl">
              {{ slotEmoji(item.slot) }}
            </div>
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-white/50 text-[10px] uppercase tracking-wider">{{ slotLabel(item.slot) }}</p>
            <h4 class="text-white font-bold text-sm truncate">{{ item.product.name }}</h4>
            <p class="text-white/50 text-xs">{{ item.reason }}</p>
          </div>
          <span class="text-yellow-400 font-bold text-sm whitespace-nowrap">
            €{{ parseFloat(item.product.price).toFixed(2) }}
          </span>
        </div>
      </div>

      <button @click="addAllToCart" class="cta-btn mb-3">
        Alles in den Warenkorb 🛒
      </button>
      <button @click="reset" class="text-white/40 text-sm hover:text-white/60 transition-colors">
        Nochmal versuchen
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useGamificationStore } from '@/stores/gamification'

const emit = defineEmits(['add-to-cart', 'close'])
const store = useGamificationStore()

const phase = ref('idle')
const loading = ref(false)
const questions = ref([])
const currentIndex = ref(0)
const answers = ref([])

const isDragging = ref(false)
const startX = ref(0)
const currentX = ref(0)
const swipeDir = ref(null)
const cardStyle = ref({})

const currentQuestion = computed(() => questions.value[currentIndex.value])
const recommendation = computed(() => store.swipeRecommendation)

async function start() {
  loading.value = true
  try {
    const sessionId = parseInt(localStorage.getItem('sessionNumericId')) || null
    const identifier = localStorage.getItem('sessionUniqueId') || localStorage.getItem('sessionId') || ''
    const profileId = parseInt(localStorage.getItem('guestProfileId')) || null

    let data = null
    try {
      data = await store.startFoodSwipe(sessionId, identifier, profileId)
    } catch (e) {
      // Ignore feature flag check, try direct API call
    }

    if (!data || (!data.questions && !store.swipeQuestions.length)) {
      const { gamificationService } = await import('@/services/api/gamificationService')
      const resp = await gamificationService.startFoodSwipe({
        orderSessionId: sessionId, sessionIdentifier: identifier, guestProfileId: profileId
      })
      data = resp.data
      store.swipeSession = data.swipeSession
      store.swipeQuestions = data.questions || []
    }

    questions.value = data?.questions || store.swipeQuestions || []
    if (questions.value.length === 0) {
      questions.value = [
        { question: 'Magst du es herzhaft und deftig?', emoji: '🍔', yesTag: 'hearty', noTag: 'light' },
        { question: 'Hast du Lust auf etwas Scharfes?', emoji: '🌶️', yesTag: 'spicy', noTag: 'mild' },
        { question: 'Wäre ein kühles Getränk jetzt perfekt?', emoji: '🥤', yesTag: 'beverage', noTag: 'no_drink' },
        { question: 'Bist du ein Süßspeisen-Fan?', emoji: '🍰', yesTag: 'sweet', noTag: 'savory' },
        { question: 'Magst du Chicken?', emoji: '🍗', yesTag: 'chicken', noTag: 'no_chicken' },
      ]
    }
    phase.value = 'swiping'
    currentIndex.value = 0
    answers.value = []
  } catch (e) {
    console.error('Food swipe start failed:', e)
    questions.value = [
      { question: 'Magst du es herzhaft und deftig?', emoji: '🍔', yesTag: 'hearty', noTag: 'light' },
      { question: 'Hast du Lust auf etwas Scharfes?', emoji: '🌶️', yesTag: 'spicy', noTag: 'mild' },
      { question: 'Wäre ein kühles Getränk jetzt perfekt?', emoji: '🥤', yesTag: 'beverage', noTag: 'no_drink' },
      { question: 'Bist du ein Süßspeisen-Fan?', emoji: '🍰', yesTag: 'sweet', noTag: 'savory' },
      { question: 'Magst du Chicken?', emoji: '🍗', yesTag: 'chicken', noTag: 'no_chicken' },
    ]
    phase.value = 'swiping'
    currentIndex.value = 0
    answers.value = []
  } finally {
    loading.value = false
  }
}

function answerYes() {
  if (!currentQuestion.value) return
  answers.value.push({ question: currentQuestion.value, answer: 'yes', tag: currentQuestion.value.yesTag })
  animateSwipe('right')
}

function answerNo() {
  if (!currentQuestion.value) return
  answers.value.push({ question: currentQuestion.value, answer: 'no', tag: currentQuestion.value.noTag })
  animateSwipe('left')
}

function animateSwipe(dir) {
  const exitX = dir === 'right' ? 400 : -400
  cardStyle.value = {
    transform: `translateX(${exitX}px) rotate(${exitX * 0.08}deg)`,
    transition: 'transform 0.35s ease, opacity 0.35s ease',
    opacity: 0,
  }

  setTimeout(() => {
    swipeDir.value = null
    cardStyle.value = {}
    currentIndex.value++
    if (currentIndex.value >= questions.value.length) {
      submitAnswers()
    }
  }, 380)
}

async function submitAnswers() {
  phase.value = 'analyzing'
  try {
    const liked = answers.value.filter(a => a.answer === 'yes').map(a => a.tag)
    const disliked = answers.value.filter(a => a.answer === 'no').map(a => a.tag)
    await store.submitSwipeResults({ liked, disliked })
    phase.value = 'results'
  } catch (e) {
    console.error('Swipe submit failed:', e)
    phase.value = 'idle'
  }
}

function addAllToCart() {
  if (!recommendation.value?.items?.length) return
  for (const item of recommendation.value.items) {
    if (item.product) emit('add-to-cart', item.product)
  }
  emit('close')
}

function reset() {
  store.resetSwipe()
  phase.value = 'idle'
  currentIndex.value = 0
  answers.value = []
  questions.value = []
}

function slotEmoji(slot) {
  return { main: '🍽️', drink: '🥤', extra: '🍰' }[slot] || '🍽️'
}
function slotLabel(slot) {
  return { main: 'Hauptgericht', drink: 'Getränk', extra: 'Extra' }[slot] || ''
}

function getProductImage(product) {
  const url = product.imageUrl || product.image_url
  if (!url) return null
  if (url.startsWith('http')) return url
  return null
}

function onTouchStart(e) {
  isDragging.value = true
  startX.value = e.touches[0].clientX
}
function onTouchMove(e) {
  if (!isDragging.value) return
  currentX.value = e.touches[0].clientX - startX.value
  updateCard(currentX.value)
}
function onTouchEnd() {
  isDragging.value = false
  if (Math.abs(currentX.value) > 80) {
    if (currentX.value > 0) answerYes()
    else answerNo()
  } else {
    cardStyle.value = { transform: 'translateX(0) rotate(0)', transition: 'transform 0.3s ease' }
    swipeDir.value = null
  }
  currentX.value = 0
}
function onMouseDown(e) {
  isDragging.value = true
  startX.value = e.clientX
  const move = (ev) => { currentX.value = ev.clientX - startX.value; updateCard(currentX.value) }
  const up = () => {
    isDragging.value = false
    document.removeEventListener('mousemove', move)
    document.removeEventListener('mouseup', up)
    if (Math.abs(currentX.value) > 80) {
      if (currentX.value > 0) answerYes()
      else answerNo()
    } else {
      cardStyle.value = { transform: 'translateX(0) rotate(0)', transition: 'transform 0.3s ease' }
      swipeDir.value = null
    }
    currentX.value = 0
  }
  document.addEventListener('mousemove', move)
  document.addEventListener('mouseup', up)
}
function updateCard(dx) {
  cardStyle.value = {
    transform: `translateX(${dx}px) rotate(${dx * 0.08}deg)`,
    transition: isDragging.value ? 'none' : 'transform 0.3s ease',
  }
  if (dx > 40) swipeDir.value = 'right'
  else if (dx < -40) swipeDir.value = 'left'
  else swipeDir.value = null
}
</script>

<style scoped>
.food-tinder-container {
  @apply flex flex-col items-center py-6 px-4;
}
.start-screen, .analyzing-screen, .results-screen {
  @apply flex flex-col items-center text-center;
}
.start-btn {
  @apply px-8 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold rounded-full;
  @apply hover:from-orange-400 hover:to-red-400 transition-all duration-300 transform hover:scale-105;
}
.start-btn:disabled { @apply opacity-50 cursor-not-allowed; }

.progress-dots {
  @apply flex gap-2 justify-center;
}
.dot {
  @apply w-2.5 h-2.5 rounded-full transition-all duration-300;
}
.dot-done { @apply bg-green-400 scale-90; }
.dot-active { @apply bg-yellow-400 scale-125; }
.dot-upcoming { @apply bg-white/20; }

.card-stack {
  @apply relative w-full max-w-xs;
  min-height: 220px;
}
.question-card {
  @apply relative w-full p-8 rounded-3xl;
  @apply bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm;
  @apply border border-white/10 shadow-2xl;
  @apply flex flex-col items-center justify-center cursor-grab active:cursor-grabbing select-none;
  min-height: 220px;
}

.swipe-label {
  @apply absolute top-4 px-4 py-1 rounded-xl text-lg font-black border-4;
}
.yes-label {
  @apply left-4 bg-green-500/80 text-white border-green-400 -rotate-12;
}
.no-label {
  @apply right-4 bg-red-500/80 text-white border-red-400 rotate-12;
}

.action-buttons {
  @apply flex gap-6 justify-center;
}
.answer-btn {
  @apply w-16 h-16 rounded-full flex flex-col items-center justify-center transition-all duration-200;
  @apply hover:scale-110 active:scale-95;
}
.no-btn {
  @apply bg-white/5 border-2 border-red-400/60 text-red-400;
  @apply hover:bg-red-500/20;
}
.yes-btn {
  @apply bg-white/5 border-2 border-green-400/60 text-green-400;
  @apply hover:bg-green-500/20;
}

.analyzing-animation {
  @apply relative w-20 h-20 flex items-center justify-center;
}
.analyze-ring {
  @apply absolute inset-0 border-4 border-yellow-400/30 border-t-yellow-400 rounded-full animate-spin;
}

.result-item {
  @apply flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10;
}
.result-item-icon {
  @apply w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center text-xl flex-shrink-0;
}

.cta-btn {
  @apply w-full py-3 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500;
  @apply text-gray-900 font-bold text-center;
  @apply hover:from-amber-300 hover:to-orange-400 transition-all duration-300;
}
</style>
